import { useMemo, useState } from "react";
import { Printer, Trash2, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmt, fmtRupiah } from "@/lib/feedModels";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function HistorySection({ history, loading, onPrint, onDelete, showCost = true }) {
  const [search, setSearch] = useState("");
  const [modelFilter, setModelFilter] = useState("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter((item) => {
      const matchModel = modelFilter === "all" || item.feed_model === modelFilter;
      const matchSearch =
        !q ||
        (item.name && item.name.toLowerCase().includes(q)) ||
        item.model_label.toLowerCase().includes(q);
      return matchModel && matchSearch;
    });
  }, [history, search, modelFilter]);

  const modelOptions = useMemo(() => {
    const map = new Map();
    history.forEach((h) => {
      if (!map.has(h.feed_model)) map.set(h.feed_model, h.model_label);
    });
    return Array.from(map, ([id, label]) => ({ id, label }));
  }, [history]);
  const BADGE = "bg-[#FBEAD6] text-[#B87148] border-[#D38B60]";

  return (
    <div
      data-testid="history-section"
      className="col-span-full mt-2 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#E0A988]/40"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F7E5D2] flex items-center justify-center">
            <History className="w-4 h-4 text-[#A06E4D]" />
          </div>
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-[#8A5230]">
              Riwayat Perhitungan
            </h2>
            <p className="text-xs text-[#A06E4D]">
              Menampilkan {filtered.length} dari {history.length} perhitungan
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C99A6A]" />
            <Input
              data-testid="history-search"
              type="text"
              placeholder="Cari nama sapi / kandang..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 pl-9 w-full sm:w-64 bg-[#FBF3E8] border-[#E0A988]/50 focus-visible:ring-[#A06E4D]"
            />
          </div>
          <Select value={modelFilter} onValueChange={setModelFilter}>
            <SelectTrigger
              data-testid="history-model-filter"
              className="h-10 w-full sm:w-48 bg-[#FBF3E8] border-[#E0A988]/50 focus:ring-[#A06E4D]"
            >
              <SelectValue placeholder="Semua model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-option-all">
                Semua Model
              </SelectItem>
              {modelOptions.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`filter-option-${m.id}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p data-testid="history-loading" className="text-sm text-[#C99A6A] py-6 text-center">
          Memuat riwayat...
        </p>
      ) : history.length === 0 ? (
        <div
          data-testid="history-empty"
          className="rounded-xl border border-dashed border-[#E0A988]/60 bg-[#FBF3E8] p-10 text-center text-sm text-[#C99A6A]"
        >
          Belum ada riwayat. Simpan perhitungan pertama Anda.
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="history-no-match"
          className="rounded-xl border border-dashed border-[#E0A988]/60 bg-[#FBF3E8] p-10 text-center text-sm text-[#C99A6A]"
        >
          Tidak ada riwayat yang cocok dengan pencarian/filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#F0E3D2]">
          <Table data-testid="history-table">
            <TableHeader>
              <TableRow className="bg-[#FBF3E8]">
                <TableHead className="text-xs font-semibold">Tanggal</TableHead>
                <TableHead className="text-xs font-semibold">Nama Sapi / Kandang</TableHead>
                <TableHead className="text-xs font-semibold">Model Pakan</TableHead>
                <TableHead className="text-xs font-semibold text-right">Bobot Sapi</TableHead>
                <TableHead className="text-xs font-semibold text-right">Total Pakan</TableHead>
                {showCost && (
                  <TableHead className="text-xs font-semibold text-right">Biaya/Bulan</TableHead>
                )}
                <TableHead className="text-xs font-semibold text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                return (
                  <TableRow key={item.id} data-testid={`history-row-${item.id}`}>
                    <TableCell className="text-xs text-[#A06E4D] whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell className="text-sm text-[#7A4A28] font-medium">
                      {item.name || <span className="text-[#C99A6A] italic font-normal">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`${BADGE} font-medium whitespace-nowrap`}
                      >
                        {item.model_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm text-[#7A4A28]">
                      {fmt(item.total_weight)} kg
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm font-bold text-[#7A4A28]">
                      {fmt(item.total_feed)} kg
                    </TableCell>
                    {showCost && (
                      <TableCell className="text-right font-mono-num text-sm text-[#8A5230]">
                        {item.monthly_cost > 0 ? fmtRupiah(item.monthly_cost) : "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`btn-print-history-${item.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => onPrint(item)}
                          className="h-8 w-8 text-[#A06E4D] hover:text-[#8A5230] hover:bg-[#F7E5D2]"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`btn-delete-history-${item.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(item.id)}
                          className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
